/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import Quill from 'quill';
import Delta from 'quill-delta';
import Toolbar from 'quill/modules/toolbar';
import { EmbeddedImageService } from './embedded-image.service';
import { PlatformImageEmbed } from './+blots/platform-image-embed';

const Module = Quill.import('core/module');

// Supported web-compatible image formats
const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const SUPPORTED_IMAGE_EXTENSIONS = '.jpg, .jpeg, .png, .gif, .webp';

export interface NgfRichTextImageExtensionOptions {
    embeddedImageService: EmbeddedImageService;
    onUnsupportedFileError?: (count: number, supportedFormats: string) => void;
}

/**
 * Image Extension for NgfRichTextEditor
 *
 * This extension provides embedded image support for the rich text editor,
 * handling image insertion via toolbar button, drag-and-drop, and paste.
 *
 * The `embeddedImageService` must be provided via Angular DI in the host component
 * and passed through the Quill module options.
 */
export class NgfRichTextImageExtension extends Module<NgfRichTextImageExtensionOptions> {

    quill: Quill;
    private embeddedImageService: EmbeddedImageService;
    private onUnsupportedFileError?: (count: number, supportedFormats: string) => void;

    static DEFAULTS: NgfRichTextImageExtensionOptions = {
        embeddedImageService: null,
        onUnsupportedFileError: undefined
    };

    /**
     * Register the image extension with Quill.
     * Registers the PlatformImageEmbed blot so Quill knows how to render embedded images.
     */
    static register(): void {
        Quill.register(PlatformImageEmbed);
    }

    // note: options are passed in via the Quill editor config when the editor is instantiated
    // e.g. for this module, we must have an `image` member in the `modules` config object
    constructor(quill: Quill, options: Partial<NgfRichTextImageExtensionOptions>) {
        super(quill, options);

        this.embeddedImageService = options.embeddedImageService;
        this.onUnsupportedFileError = options.onUnsupportedFileError;

        this.setupImageHandlers();
    }

    private setupImageHandlers(): void {
        const toolbar = this.quill.getModule('toolbar') as Toolbar;
        if (toolbar) {
            toolbar.addHandler('image', () => this.handleImageButtonClick());
        }

        this.setupEmbeddedImageMatcher();
        this.setupDragAndDrop();
        this.setupPasteHandling();
    }

    private handleImageButtonClick(): void {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = SUPPORTED_IMAGE_EXTENSIONS;
        input.multiple = true;

        input.onchange = (event: any) => {
            const files = event.target.files;
            if (files && files.length > 0) {
                this.processFiles(Array.from(files) as File[]);
            }
        };

        input.click();
    }

    private setupDragAndDrop(): void {
        this.quill.root.addEventListener('drop', (e: DragEvent) => {
            const files = Array.from(e.dataTransfer?.files || []);
            if (files.length > 0) {
                e.preventDefault();
                e.stopImmediatePropagation();
                this.processFiles(files);
            }
        }, true);

        this.quill.root.addEventListener('dragover', (e: DragEvent) => {
            e.preventDefault();
        }, true);
    }

    private setupEmbeddedImageMatcher(): void {
        this.quill.clipboard.addMatcher('IMG', (node: HTMLImageElement, delta: Delta) => {
            const attributes = PlatformImageEmbed.extractAttributesFromNode(node);
            return new Delta().insert({ [PlatformImageEmbed.blotName]: attributes });
        });
    }

    private setupPasteHandling(): void {
        this.quill.root.addEventListener('paste', (e: ClipboardEvent) => {
            const files = Array.from(e.clipboardData?.items || [])
                .filter(item => item.kind === 'file')
                .map(item => item.getAsFile());

            if (files.length > 0) {
                e.preventDefault();
                this.processFiles(files);
            }
        });
    }

    private processFiles(files: File[]): void {
        const imageFiles = files.filter(file => SUPPORTED_IMAGE_TYPES.includes(file.type));
        const unsupportedFiles = files.filter(file => !SUPPORTED_IMAGE_TYPES.includes(file.type));

        imageFiles.forEach(file => this.insertImage(file));

        if (unsupportedFiles.length > 0 && this.onUnsupportedFileError) {
            this.onUnsupportedFileError(unsupportedFiles.length, SUPPORTED_IMAGE_EXTENSIONS);
        }
    }

    private async insertImage(file: File): Promise<void> {
        if (!this.embeddedImageService) {
            throw new Error('[NgfRichTextImageExtension] EmbeddedImageService is required in editor config');
        }

        const pendingId = await this.embeddedImageService.addPendingImage(file);
        const pendingImage = this.embeddedImageService.getPendingImage(pendingId);

        if (pendingImage?.dataUrl) {
            const selection = this.quill.getSelection() || { index: this.quill.getLength() };

            this.quill.insertEmbed(selection.index, PlatformImageEmbed.blotName, {
                src: pendingImage.dataUrl,
                pendingId
            }, 'user');

            this.quill.setSelection(selection.index + 1);
        }
    }
}
